import React from 'react';
import { Flex, Box } from 'rebass';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import Image from '../Image/Image';
import Link from '../../base/Link/Link';
import Typography from '../Typography/Typography';

import parseNews from '../../utils/parseNews';
import createMemo from '../../utils/storyblokRelationsMemo';
import { FeaturedNewsCardStoryblok } from '../../types/storyblok';

dayjs.extend(relativeTime);

const newsItemMemo = createMemo({});

/**
 * Renders a <FeaturedNewsCardView /> component
 * @param {Object} props
 * @param {string} props.datePublished - Published date in ISO format
 * @param {string} [props.image] - Image source
 * @param {string} [props.link] - News href
 * @param {string} [props.publisherName] - Name of the publisher
 * @param {string} [props.text] - News description
 * @param {string} props.title - News title
 */
const FeaturedNewsCardView = ({
  datePublished,
  image,
  link,
  publisherName,
  text,
  title,
}: FeaturedNewsCardStoryblok) => (
  <Box>
    {image ? (
      <Image blurLoad minSize="200x0" src={image} className="mb-3" />
    ) : null}
    <Link
      href={link}
      className="font-foundersGrotesk text-2xl mb-4 hover:opacity-80"
    >
      {title}
    </Link>
    <Flex flexWrap="wrap" my={2}>
      <Typography component="strong" className="font-foundersGrotesk">
        {publisherName}
      </Typography>
      <Typography
        alignItems="center"
        component="time"
        dateTime={datePublished}
        display="flex"
        variant="body-small"
        className="font-foundersGrotesk opacity-60"
      >
        {publisherName ? '\u00A0 — ' : ''}
        {dayjs(datePublished).fromNow()}
      </Typography>
    </Flex>
    <Typography
      sx={{
        '&:last-child': {
          marginBottom: 3,
        },
      }}
      className="font-foundersGrotesk"
    >
      {text}
    </Typography>
  </Box>
);

/**
 * Processes the information to render <FeaturedNewsCardView />
 * @param {Object} props
 * @param {Object} props.newsItem - News item or Page data
 */
const FeaturedNewsCard = ({ newsItem: newsItemRel }) => {
  const newsItem = newsItemMemo(newsItemRel);

  const parsedNews = parseNews(newsItem);

  return <FeaturedNewsCardView {...parsedNews} />;
};

export default FeaturedNewsCard;
